/**
 * 69.38 (ARCHITECTURE §5.28.1, §5.28's step 2; DECISIONS 252 (5), 274), with GameConfig.mountedAssault: the mounted assault's
 * convoy — its vehicles march in column to the jump-off, at a spacing, and fan out there.
 * - **Order of march** (274 (d), the owner: "Tank leads, but not opposed to allowing 1-2 technicals scout ahead if it's big
 *   formation with excess"): the tanks first, the APCs in the middle, the technicals after. With a tank and
 *   CONVOY_MARCH.scoutFrom technicals or more, one technical scouts ahead of it (two from `scoutTwoFrom`), `scoutGap` spacings
 *   out; with no tank, a technical leads; with neither, the first APC.
 * - **The follow:** the head (the first member standing with its driver seated; a mobility kill drops out) steers for the halt point (the first APC's
 *   jump-off). Every other member steers for a point its place's gap back along the head's trail (`trail`, the head's
 *   positions laid every `trailStepPeople` it goes, newest first), and holds where that point is not yet laid. All at the
 *   column's pace (its slowest member's top speed × `paceShare`); a follower more than `catchUp` spacings off its point at
 *   its own top. The head holds while a member lags more than `lagGaps` spacings off its point — an APC every time, a tank or
 *   technical one spell of `waitSeconds` a march at most (the assembly: a squad boarding its APC); a laggard after that catches up at its top, or — a tank or
 *   technical, never an APC — past `dropGaps` spacings falls out of the column (`dropped`) to its post; and any laggard, an
 *   APC too, that goes `stuckSeconds` without closing `closePeople` on its point (no driver, stuck, wandering). A tank or
 *   technical marches in one column: a second play's convoy leaves out those already marching.
 * - **Spacing** (274 (a)): `spacingOwn` people on the side's own ground, `spacingPast` past it — core/ground.ts territory at
 *   the head (no rule there: own while the head is nearer the side's home than the objective).
 * - **The halt** (274 (b), a wedge at a planned halt): the head within `haltPeople` of the halt point. The APCs take a wedge's
 *   slots: the first at the halt point, the rest back and out to either side, `wedgeShare` of a spacing a step; each squad's
 *   ride and jump-off move to its APC's slot. The fire support goes on to its posts (its own loop).
 * - **Contact on the march** (274 (c)): a member hit, or an enemy its side knows within `contactPeople` of one. The column
 *   halts by `contactDrill` — the hook for the drill by the column and the contact (274 (c): "maybe depending on composition
 *   and scale of contact eventually"); today always a herringbone: each APC pulls off `herringPeople` where it stands, to the
 *   side away from the contact, and its squad dismounts there. The fire support fights from where it is. **The re-form**
 *   (274 (c), B: "the play re-forms on the column once the contact is gone"; `reformConvoy`): `clearSeconds` with no contact
 *   and the halt is lifted — each riding squad's jump-off back to its own before the drill, its ride re-tasked there (it
 *   boards), and the column marches on to its halt point from where it stands. Without it a herringbone was the column's end:
 *   on the large planet (lab-7) both sides' escorts met contact 26 and 33 s out, sat, and their plays dissolved at 60 s.
 * - **The assembly** (GameConfig.convoyAssembly, off by default; DECISIONS 290 (1), the owner: "yes but also probably will be some forward assembly in the future"):
 *   before it marches the column forms. The first APC stays where it stands (its squad boards it there); the head's slot is
 *   ahead of it toward the halt, each member's its place's gap back along that line. Every member drives to its slot at its
 *   own top and holds there; the march begins when every crewed member is within `assemblePeople` of its slot and every
 *   APC crewed (a tank or technical with no driver waited for `crewSeconds`), on contact (its drill, as on the march), or
 *   `assembleSeconds` after the call (a member still out catches up at its top). With the play called while its fire
 *   support was forward at its posts and the APC at the base, the column chased itself 87 people apart (median) and more.
 *   A forward assembly area, nearer the front, is later (290 (2)).
 * Pure. `stepConvoy` runs once a second with the plays (core/plays.ts stepPlays); `convoyDrive` is the drivers' lookup a tick
 * (ai/rideBrain.ts rideIntent, ai/actionBot.ts techDrive): a walk along the trail, no search.
 */
import type { GameState, Play, StructureState, Vec3 } from '../types';
import { vecAdd, vecCross, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos, dcos, dsin } from './dmath';
import { PHYS_DT } from '../constants';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { bikeNumbers, driveSpec } from './drive';
import { territory } from './ground';
import { teamPicture } from './picture';

/** The convoy's dials (274's picks: (a) the spacings; the rest proposals). */
export const CONVOY_MARCH = {
  spacingOwn: 15, spacingPast: 25,
  scoutFrom: 3, scoutTwoFrom: 5, scoutGap: 2,
  trailStepPeople: 2, trailPoints: 200,
  paceShare: 0.9, catchUp: 1.5, lagGaps: 2, waitSeconds: 30, holdPeople: 3, closePeople: 5, stuckSeconds: 90,
  haltPeople: 20, wedgeShare: 0.7,
  contactPeople: 50, knownSeconds: 10, herringPeople: 5, clearSeconds: 10,
  /** DECISIONS 311 (the owner: "Scale + mount window"): contact is a member hit, or more than `contactFew` enemies its side knows near the column; a column re-formed from a herringbone marches `mountSeconds` before new contact halts it again */
  contactFew: 2, mountSeconds: 30,
  assemblePeople: 8, assembleSeconds: 90, crewSeconds: 30,
  dropGaps: 5,
};

export type MarchState = NonNullable<NonNullable<Play['mounted']>['convoy']>;
export type ContactDrill = 'herringbone';

const person = (state: Pick<GameState, 'config'>) => PERSON_BODY_HEIGHT * personSetOf(state).k;
const arc = (R: number, a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * R;
/** The point `d` (world units) from `a` toward `b` along the sphere (b itself when nearer). */
function toward(R: number, a: Vec3, b: Vec3, d: number): Vec3 {
  const ab = arc(R, a, b);
  if (ab <= d || ab <= 0) return vecNormalize(b);
  const t = d / ab, na = vecNormalize(a), nb = vecNormalize(b);
  return vecNormalize(vecAdd(vecScale(na, 1 - t), vecScale(nb, t)));
}

/** 274 (d): the order of march — the scouts, the tanks, the APCs (in their squads' order), the other technicals. */
export function marchOrder(state: GameState, rides: Record<number, number>, fire: readonly number[]): { order: number[]; scouts: number } {
  const type = (id: number) => state.structures.find(s => s.id === id)?.type;
  const apcs = Object.keys(rides).map(Number).sort((a, b) => a - b).map(k => rides[k]);
  const tanks = fire.filter(id => type(id) === 'tank'), techs = fire.filter(id => type(id) === 'technical');
  const scouts = tanks.length ? (techs.length >= CONVOY_MARCH.scoutTwoFrom ? 2 : techs.length >= CONVOY_MARCH.scoutFrom ? 1 : 0) : 0;
  const lead = tanks.length ? [...techs.slice(0, scouts), ...tanks] : techs.slice(0, 1);
  const rest = tanks.length ? techs.slice(scouts) : techs.slice(1);
  return { order: [...new Set([...lead, ...apcs, ...rest])], scouts };
}

/**
 * A new convoy for a mounted play: its order, halting at `halt`. A fire-support vehicle already marching in another of the
 * side's convoys stays in that one (two plays at once name the same tanks and technicals; a vehicle follows one column).
 */
export function newConvoy(state: GameState, rides: Record<number, number>, fire: readonly number[], halt: Vec3, tick: number): MarchState {
  const marching = new Set((state.plays ?? []).filter(q => q.phase === 'setting' && q.mounted?.convoy && !q.mounted.convoy.halted).flatMap(q => q.mounted!.convoy!.order));
  const m = marchOrder(state, rides, fire.filter(id => !marching.has(id)));
  const slots = state.config.convoyAssembly ? assemblySlots(state, m.order, m.scouts, halt, new Set(Object.values(rides))) : null;   // (GameConfig.convoyAssembly: off by default)
  return { ...m, halt: { ...halt }, trail: [], spacing: CONVOY_MARCH.spacingOwn, pace: 0, ...(slots ? { assembly: { slots, since: tick } } : {}) };
}

/** Each member's gap behind the head in order of march (world units), at `spacing` people (a scout's gap `scoutGap` spacings). */
function gapsInOrder(order: readonly number[], scouts: number, sp: number): Map<number, number> {
  const gaps = new Map<number, number>();
  let g = 0;
  order.forEach((id, k) => { if (k > 0) g += sp * (k - 1 < scouts && k >= scouts ? CONVOY_MARCH.scoutGap : 1); gaps.set(id, g); });
  return gaps;
}

/**
 * DECISIONS 290 (1): the column's slots at its assembly — the first APC where it stands (no APC: the head where it stands),
 * the line from there toward the halt, each member its gap (own ground's spacing) behind the head along it. Null: no line
 * (the anchor gone, or standing at the halt).
 */
function assemblySlots(state: GameState, order: readonly number[], scouts: number, halt: Vec3, apcs: ReadonlySet<number>): Record<number, Vec3> | null {
  const anchorId = order.find(id => apcs.has(id)) ?? order[0];
  const anchor = anchorId !== undefined ? standing(state, anchorId) : undefined;
  if (!anchor) return null;
  const R = state.planets[anchor.planetIndex].radius, n = vecNormalize(anchor.surfaceDir), h = vecNormalize(halt);
  const ahead = vecSub(h, vecScale(n, vecDot(h, n)));
  if (vecLength(ahead) < 1e-9) return null;
  const fwd = vecNormalize(ahead), gaps = gapsInOrder(order, scouts, CONVOY_MARCH.spacingOwn * person(state)), a0 = gaps.get(anchorId)!;
  // (a point `d` world units ahead of the anchor along the line; behind it where `d` is negative)
  const at = (d: number): Vec3 => vecNormalize(vecAdd(vecScale(n, dcos(d / R)), vecScale(fwd, dsin(d / R))));
  return Object.fromEntries(order.map(id => [id, at(a0 - gaps.get(id)!)]));
}

/** Whether the column is still forming at its assembly (DECISIONS 290 (1)). */
export function assembling(c: MarchState): boolean {
  return !!c.assembly && c.assembly.done === undefined;
}

/** A member still in the column: standing and able to drive (a mobility kill, 69.29's `disabled`, drops out — the column goes on without it). */
const standing = (state: GameState, id: number): StructureState | undefined => { const v = state.structures.find(s => s.id === id); return v && v.health > 0 && !v.disabled ? v : undefined; };
const crewed = (v: StructureState) => (v.seats ?? []).some(x => x.role === 'driver' && x.occupant !== undefined && x.occupant !== null);

/** The column now: its standing members in order, the head (the first with its driver seated), and each member's gap behind the head (world units). */
function column(state: GameState, c: MarchState): { members: StructureState[]; head: StructureState | null; gapOf: Map<number, number> } {
  const members = c.order.filter(id => !c.dropped?.includes(id)).map(id => standing(state, id)).filter((v): v is StructureState => !!v);   // (69.38: a member fallen out is not the column's)
  const head = members.find(crewed) ?? null;
  const gapOf = new Map<number, number>();
  if (!head) return { members, head, gapOf };
  const sp = c.spacing * person(state);
  let g = 0, prev = c.order.indexOf(head.id);
  for (const v of members) {
    if (v.id === head.id) continue;
    const k = c.order.indexOf(v.id);
    g += sp * (prev < c.scouts && k >= c.scouts ? CONVOY_MARCH.scoutGap : 1);
    gapOf.set(v.id, g);
    prev = k;
  }
  return { members, head, gapOf };
}

/** The point `gap` back along the head's way (its position, then its trail); `short` when the trail is not that long yet. */
function followPoint(R: number, headAt: Vec3, trail: readonly Vec3[], gap: number): { at: Vec3; short: boolean } {
  let from = headAt, left = gap;
  for (const p of trail) {
    const d = arc(R, from, p);
    if (d >= left) return { at: toward(R, from, p, left), short: false };
    left -= d; from = p;
  }
  return { at: from, short: true };
}

/** What a marching member's driver does this tick: hold, or steer for `goal` at `pace` (world units a second) at most. Null: not in a marching column. */
export function convoyDrive(state: GameState, vehicleId: number): { goal: Vec3; pace: number; hold: boolean } | null {
  if (!state.config.mountedAssault || !state.plays) return null;
  const p = state.plays.find(q => q.phase === 'setting' && q.mounted?.convoy && !q.mounted.convoy.halted && q.mounted.convoy.order.includes(vehicleId) && !q.mounted.convoy.dropped?.includes(vehicleId));   // (69.38: fallen out — its own ride or post)
  const c = p?.mounted?.convoy;
  // (DECISIONS 290 (1): forming up — to its slot at its own top, held there)
  if (p && c && assembling(c)) {
    const me = standing(state, vehicleId), slot = c.assembly!.slots[vehicleId];
    if (!me) return null;
    if (!slot || me.planetIndex !== p.position.planetIndex) return { goal: vecNormalize(me.surfaceDir), pace: 0, hold: true };
    const off = arc(state.planets[me.planetIndex].radius, me.surfaceDir, slot);
    return { goal: vecNormalize(slot), pace: bikeNumbers('gas', personSetOf(state).walkSpeed, driveSpec(me.type)).top, hold: off <= CONVOY_MARCH.holdPeople * person(state) };
  }
  if (!p || !c || !c.trail.length) return c && p ? { goal: vecNormalize(c.halt), pace: 0, hold: true } : null;
  const { head, gapOf } = column(state, c);
  const me = standing(state, vehicleId);
  if (!head || !me) return null;
  const R = state.planets[me.planetIndex].radius, u = person(state);
  if (me.id === head.id) return { goal: vecNormalize(c.halt), pace: c.pace, hold: !!c.headHold };
  const gap = gapOf.get(me.id);
  if (gap === undefined) return null;
  const f = followPoint(R, head.surfaceDir, c.trail, gap), off = arc(R, me.surfaceDir, f.at);
  // (not yet laid: it holds where it stands, unless the trail's start is farther off than its place's gap — then it closes on it)
  if ((f.short && off <= gap) || off <= CONVOY_MARCH.holdPeople * u) return { goal: f.at, pace: c.pace, hold: true };
  const top = bikeNumbers('gas', personSetOf(state).walkSpeed, driveSpec(me.type)).top;
  return { goal: f.at, pace: off > CONVOY_MARCH.catchUp * c.spacing * u ? top : c.pace, hold: false };
}

/** 274 (c)'s hook: the drill for a contact on the march, by the column and the contact. Today a herringbone. */
export function contactDrill(_state: GameState, _c: MarchState, _contact: Vec3): ContactDrill {
  return 'herringbone';
}

/** The contact on the march: a member hit since the last look, or the nearest enemy its side knows within CONVOY_MARCH.contactPeople of a member. */
function contactOf(state: GameState, p: Play, members: readonly StructureState[], tick: number): Vec3 | null {
  const c = p.mounted!.convoy!, u = person(state);
  let best: { at: Vec3; d: number } | null = null, near = 0;
  for (const b of teamPicture(state, p.team, tick)) {
    if ((tick - b.tick) * PHYS_DT > CONVOY_MARCH.knownSeconds) continue;
    if (b.unitId !== undefined) { const e = state.players.find(q => q.id === b.unitId); if (!e || !e.alive || e.teamIndex === p.team) continue; }
    let seen = false;
    for (const v of members) {
      if (v.planetIndex !== b.planetIndex) continue;
      const d = arc(state.planets[v.planetIndex].radius, v.surfaceDir, b.surfaceDir);
      if (d > CONVOY_MARCH.contactPeople * u) continue;
      seen = true;
      if (!best || d < best.d) best = { at: b.surfaceDir, d };
    }
    if (seen) near++;
  }
  // (DECISIONS 311: real contact — more than a few known near it; a lone scout or two at 40 people held lab-7's column halted
  // 180 s, re-halted 10–20 s after each re-form, its riders on foot)
  if (best && near > CONVOY_MARCH.contactFew) return best.at;
  const hit = members.find(v => c.health?.[v.id] !== undefined && v.health < c.health[v.id]);
  return hit ? hit.surfaceDir : null;
}

/**
 * 274 (c), B ("the play re-forms on the column once the contact is gone"): once a second, a column halted on contact (the
 * herringbone). Contact — an enemy its side knows within `contactPeople` of a member, or a member hit since the last look —
 * keeps it halted; `clearSeconds` with none and it re-forms: the halt lifted (the march goes on from where the head stands,
 * on its trail, to its halt point) and each riding squad's jump-off before the drill returned, for the play to re-task its
 * ride there. Any other column as it is.
 */
export function reformConvoy(state: GameState, p: Play, tick: number): { convoy: MarchState; jumpOff?: Record<number, Vec3> } {
  const c = p.mounted!.convoy!, h = c.halted;
  if (h?.how !== 'herringbone') return { convoy: c };
  const { members } = column(state, c);
  const health = Object.fromEntries(members.map(v => [v.id, v.health]));
  if (contactOf(state, p, members, tick)) { const { clear: _, ...held } = h; return { convoy: { ...c, health, halted: held } }; }
  const clear = h.clear ?? tick;
  if ((tick - clear) * PHYS_DT < CONVOY_MARCH.clearSeconds) return { convoy: { ...c, health, halted: { ...h, clear } } };
  const { halted: _, ...marching } = c;
  return { convoy: { ...marching, health, mountUntil: tick + Math.round(CONVOY_MARCH.mountSeconds / PHYS_DT) }, jumpOff: h.jumpOff ?? {} };
}

/** The side's home's direction (its first base, else its first unit's spot) — for the spacing where the ground has no rule. */
function homeOf(state: GameState, team: number): Vec3 | null {
  return state.actionHomes?.[team] ?? state.players.find(p => p.teamIndex === team)?.surfaceDir ?? null;
}

/**
 * Once a second, a marching convoy's look: the head's trail laid, the spacing and the pace, the head held for a lagging member,
 * and the halt — at the halt point (a wedge) or on contact (`contactDrill`). Returns the convoy's new state and, at a halt,
 * each riding squad's new jump-off (its APC's slot), for the play to re-task.
 */
export function stepConvoy(state: GameState, p: Play, objective: Vec3, tick: number): { convoy: MarchState; slots?: Record<number, Vec3> } {
  let c0 = p.mounted!.convoy!;
  if (c0.halted) return { convoy: c0 };
  // DECISIONS 290 (1): forming at its assembly — on when every crewed member is at its slot and every ride crewed (its squad
  // aboard to drive it); a tank or technical with no driver is waited for `crewSeconds` (its crew boarding: at a story's
  // start nobody is aboard), then the column goes without it, as on the march; or the wait is out. Contact (274 (c)) ends it,
  // its drill as on the march
  if (assembling(c0)) {
    const a = c0.assembly!, u = person(state), { members } = column(state, c0), rides = new Set(Object.values(p.mounted!.rides));
    const formed = members.every(v => {
      const slot = a.slots[v.id];
      return crewed(v) ? !!slot && arc(state.planets[v.planetIndex].radius, v.surfaceDir, slot) <= CONVOY_MARCH.assemblePeople * u
        : !rides.has(v.id) && (tick - a.since) * PHYS_DT >= CONVOY_MARCH.crewSeconds;
    });
    const contact = !formed && contactOf(state, { ...p, mounted: { ...p.mounted!, convoy: c0 } }, members, tick);
    // (its members' health kept from here, so a hit while it forms is contact at the next look)
    if (!formed && !contact && (tick - a.since) * PHYS_DT < CONVOY_MARCH.assembleSeconds) return { convoy: { ...c0, health: Object.fromEntries(members.map(v => [v.id, v.health])) } };
    c0 = { ...c0, assembly: { ...a, done: tick } };
  }
  const { members, head } = column(state, c0);
  if (!head) return { convoy: c0 };
  const planet = state.planets[head.planetIndex], R = planet.radius, u = person(state);
  // the trail (a new head: laid afresh from it)
  let trail = c0.trail;
  if (c0.head !== head.id || !trail.length) trail = [{ ...head.surfaceDir }];
  else if (arc(R, head.surfaceDir, trail[0]) >= CONVOY_MARCH.trailStepPeople * u) trail = [{ ...head.surfaceDir }, ...trail].slice(0, CONVOY_MARCH.trailPoints);
  // the spacing by the ground at the head; the pace its slowest member's
  const ground = territory(state, p.team, { planetIndex: head.planetIndex, surfaceDir: head.surfaceDir });
  const home = homeOf(state, p.team);
  const own = ground ? ground === 'owned' : !!home && arc(R, head.surfaceDir, home) < arc(R, head.surfaceDir, objective);
  const spacing = own ? CONVOY_MARCH.spacingOwn : CONVOY_MARCH.spacingPast;
  const walk = personSetOf(state).walkSpeed;
  const pace = CONVOY_MARCH.paceShare * Math.min(...members.map(v => bikeNumbers('gas', walk, driveSpec(v.type)).top));
  let c: MarchState = { ...c0, head: head.id, trail, spacing, pace, health: Object.fromEntries(members.map(v => [v.id, v.health])) };
  // a lagging member holds the head (the assembly too): a tank or technical one spell of waitSeconds a march at most, from
  // the first lag; an APC every time while it closes on its place (69.38: the column is its riders' — after the spell the
  // head drove on from a lagging APC and the pair stood 1,000 people apart; the play's march has its own limit,
  // MOUNTED.marchSeconds)
  const { gapOf } = column(state, c);
  const carriers = new Set(Object.values(p.mounted!.rides));
  const offOf = (v: StructureState): number | null => {
    const g = gapOf.get(v.id);
    if (g === undefined) return null;
    const f = followPoint(R, head.surfaceDir, trail, g);
    return f.short ? null : arc(R, v.surfaceDir, f.at);
  };
  const lagging0 = members.filter(v => { const off = offOf(v); return off !== null && off > CONVOY_MARCH.lagGaps * spacing * u; });
  // 69.38's guard (69.61, lane C): a laggard not closing on its place — no driver at its wheel, stuck, or wandering — falls
  // out of the column once it has gone `stuckSeconds` without closing `closePeople` on it, an APC too (its squad its own ride
  // from there): on 6a5e54db story-1 a 6×6 lagged its tank from the start, the head held for it two looks in three — 108 s
  // at a stretch, 104 looks with nobody at its wheel — and four plays came and went with the escort never done
  const closing: Record<number, { off: number; since: number }> = {};
  for (const v of lagging0) {
    const off = offOf(v)! / u, was = c0.closing?.[v.id];
    closing[v.id] = was && was.off - off < CONVOY_MARCH.closePeople ? was : { off, since: tick };
  }
  const stuck = lagging0.filter(v => (tick - closing[v.id].since) * PHYS_DT >= CONVOY_MARCH.stuckSeconds).map(v => v.id);
  const lagging = lagging0.filter(v => !stuck.includes(v.id));
  { const { closing: _, ...rest } = c; c = Object.keys(closing).length ? { ...rest, closing } : rest; }
  if (stuck.length) c = { ...c, dropped: [...(c.dropped ?? []), ...stuck] };
  const lag = lagging.length > 0, rideLags = lagging.some(v => carriers.has(v.id));
  const waitSince = c0.waitSince ?? (lag ? tick : undefined);
  c = { ...c, ...(waitSince !== undefined ? { waitSince } : {}), headHold: rideLags || (lag && (tick - waitSince!) * PHYS_DT < CONVOY_MARCH.waitSeconds) };
  // 69.38: the head's wait spent, a tank or technical more than `dropGaps` spacings off its point falls out of the column —
  // to its post — so it neither holds the column's gaps open nor chases it (one uncrewed when the column left, crewed later
  // far behind; one stalled at home): on the ½ board a column's p90 gap was hundreds of people, most of its lagging
  // follower-seconds a member with no driver. Never an APC: the column is its riders' (one dropped while its squad boarded
  // rode alone, stalled at home, and its riders walked)
  if (waitSince !== undefined && (tick - waitSince) * PHYS_DT >= CONVOY_MARCH.waitSeconds) {
    const out = members.filter(v => {
      if (carriers.has(v.id) || stuck.includes(v.id)) return false;
      const g = gapOf.get(v.id);
      if (g === undefined) return false;
      const f = followPoint(R, head.surfaceDir, trail, g);
      return !f.short && arc(R, v.surfaceDir, f.at) > CONVOY_MARCH.dropGaps * spacing * u;
    }).map(v => v.id);
    if (out.length) c = { ...c, dropped: [...(c.dropped ?? []), ...out] };
  }
  // the halt: at the halt point a wedge, on contact the drill
  const rides = p.mounted!.rides, squadOfApc = new Map(Object.entries(rides).map(([sq, v]) => [v, Number(sq)]));
  const apcs = members.filter(v => squadOfApc.has(v.id) && !c.dropped?.includes(v.id));
  const n = vecNormalize(c.halt);
  // (DECISIONS 311: re-formed, its riders mount and it moves for `mountSeconds` — no new contact halts it in that window)
  const contact = c.mountUntil !== undefined && tick < c.mountUntil ? null : contactOf(state, { ...p, mounted: { ...p.mounted!, convoy: c0 } }, members, tick);
  if (contact && contactDrill(state, c, contact) === 'herringbone') {
    const slots: Record<number, Vec3> = {};
    for (const v of apcs) {
      const at = vecNormalize(v.surfaceDir), away = vecSub(at, vecNormalize(contact));
      const side = vecLength(away) > 1e-9 ? vecNormalize(vecSub(away, vecScale(at, vecDot(away, at)))) : vecNormalize(vecCross(at, n));
      slots[squadOfApc.get(v.id)!] = vecNormalize(vecAdd(at, vecScale(side, (CONVOY_MARCH.herringPeople * u) / R)));
    }
    return { convoy: { ...c, halted: { tick, how: 'herringbone', jumpOff: { ...p.jumpOff } } }, slots };
  }
  if (arc(R, head.surfaceDir, c.halt) <= CONVOY_MARCH.haltPeople * u) {
    const ahead = vecSub(vecNormalize(objective), vecScale(n, vecDot(vecNormalize(objective), n)));
    const axis = vecLength(ahead) > 1e-9 ? vecNormalize(ahead) : vecNormalize(vecCross(n, { x: 0, y: 0, z: 1 }));
    const across = vecNormalize(vecCross(n, axis)), step = (CONVOY_MARCH.wedgeShare * spacing * u) / R;
    const slots: Record<number, Vec3> = {};
    apcs.forEach((v, i) => {
      const k = Math.ceil(i / 2), sgn = i % 2 === 1 ? 1 : -1;
      slots[squadOfApc.get(v.id)!] = i === 0 ? { ...n } : vecNormalize(vecAdd(n, vecAdd(vecScale(axis, -k * step), vecScale(across, sgn * k * step))));
    });
    return { convoy: { ...c, halted: { tick, how: 'wedge' } }, slots };
  }
  return { convoy: c };
}
